import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AccordionItem } from "@krea/shared/design-system/AccordionItem";
import { AdvisoryBookingModal } from "@krea/shared/design-system/AdvisoryBookingModal";
import { Button, buttonVariants } from "@krea/shared/design-system/Button";
import { Text } from "@krea/shared/design-system/Text";
import { useFormatCurrency } from "@krea/shared/hooks/useFormatCurrency";
import { CountryCode } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";
import { resolveWidgetBookingUrl } from "@krea/shared/utils/resolveWidgetBookingUrl";

import fiAdvisorImageRaw from "../images/fi_advisor.webp";
import seAdvisorImageRaw from "../images/se_advisor.webp";
import type { ILoanWidgetResultProps } from "../LoanWidgetResult";
import { resolveImageSrc } from "../utils";
import { CompanyIndicator } from "./CompanyIndicator";
import { ForetagarnaResultBlock } from "./ForetagarnaResultBlock";
import { MakeAnotherEstimateButton } from "./MakeAnotherEstimateButton";

const fiAdvisorImage = resolveImageSrc(fiAdvisorImageRaw);
const seAdvisorImage = resolveImageSrc(seAdvisorImageRaw);

export const AdvisoryResult = ({
  countryCode,
  currency,
  locale,
  organisationName,
  email,
  mobilePhoneNumber,
  preliminaryBid,
  requestedLoanAmount,
  utmSourceIdentifier,
  environment,
  onGoBack,
  showCta = true,
  portalContainer,
  advisoryBooking = "newTab",
}: ILoanWidgetResultProps) => {
  const { t } = useTranslation("loanWidget");
  const [formattedCurrencyValue] = useFormatCurrency(locale, { currency });
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const bookingUrl = resolveWidgetBookingUrl(countryCode, {
    customerCompanyId: preliminaryBid?.customerCompanyId,
    companyName: organisationName,
    email,
    mobilePhoneNumber,
    environment,
  });
  const ctaClassName = "w-full bg-primary hover:bg-primary/90 mt-4";

  const maxAccuracyAmount = countryCode === CountryCode.SE ? 3000000 : 300000;
  const advisoryAmountLow = countryCode === CountryCode.SE ? 2000000 : 200000;
  const advisoryAmountHigh =
    countryCode === CountryCode.SE ? 200000000 : 20000000;
  const showForetagarna =
    utmSourceIdentifier === "foretagarna_sverige_service_ab" &&
    !!preliminaryBid?.estimatedMaxLoanAmount;

  return (
    <div>
      <div data-testid="result-card" className="mb-5 rounded-2xl bg-white p-4">
        <div className="mb-5 flex items-center rounded-xl border border-primary border-solid px-4 py-3">
          <div className="flex-1" />
          <div className="flex flex-col items-center gap-1.5">
            <p className="m-0 text-center font-semibold text-black text-xl">
              {t("loanWidget.advisoryResultTitle")}
            </p>
            <CompanyIndicator organisationName={organisationName} />
          </div>
          <div className="flex flex-1 justify-end">
            {onGoBack && (
              <MakeAnotherEstimateButton
                onClick={onGoBack}
                portalContainer={portalContainer}
              />
            )}
          </div>
        </div>

        <div className="mb-4 flex items-center gap-4 rounded-xl border border-gray-200 border-solid px-4 py-4">
          <img
            src={
              countryCode === CountryCode.FI ? fiAdvisorImage : seAdvisorImage
            }
            alt=""
            aria-hidden
            className="h-auto w-14"
          />
          <div className="flex flex-col">
            <p className="m-0 mb-2 text-base-strong">
              {t("loanWidget.advisoryMeetingPrompt")}
            </p>
            <p className="text-left font-normal text-black text-sm">
              {t("loanWidget.advisoryResultDescription", {
                amount: formattedCurrencyValue(maxAccuracyAmount),
              })}
            </p>
          </div>
        </div>

        <AccordionItem
          titleStyling="font-normal text-sm"
          title={t("loanWidget.whatIsAdvisoryService")}
          className="rounded-xl border-gray-200 bg-white"
        >
          <Text className="text-sm">
            {t("loanWidget.advisoryServiceDescription1", {
              amountLow: formattedCurrencyValue(advisoryAmountLow),
              amountHigh: formattedCurrencyValue(advisoryAmountHigh),
            })}
            <br />
            <br />
            {t("loanWidget.advisoryServiceDescription2")}
            <br />
            <br />
            {t("loanWidget.advisoryServiceDescription3")}
          </Text>
        </AccordionItem>

        {showCta &&
          (advisoryBooking === "modal" ? (
            <Button
              ref={ctaRef}
              size="lg"
              className={ctaClassName}
              onClick={() => setIsBookingOpen(true)}
            >
              {t("loanWidget.bookAMeeting")}
            </Button>
          ) : (
            /* On a third-party page the booker gets a tab of its own: the
               host's stacking and scroll rules make a modal unreliable there. */
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(buttonVariants({ size: "lg" }), ctaClassName)}
            >
              {t("loanWidget.bookAMeeting")}
            </a>
          ))}
      </div>

      {advisoryBooking === "modal" && (
        <AdvisoryBookingModal
          open={isBookingOpen}
          onOpenChange={setIsBookingOpen}
          calendarUrl={bookingUrl}
          title={t("loanWidget.bookAMeetingModalTitle")}
          container={portalContainer}
          returnFocusTo={ctaRef}
          closeLabel={t("loanWidget.close")}
        />
      )}

      <ForetagarnaResultBlock
        show={showForetagarna}
        amount={requestedLoanAmount}
        locale={locale}
        currency={currency}
      />
    </div>
  );
};
